import React, {forwardRef, useRef, useImperativeHandle, useId} from 'react';
import {Swiper, SwiperSlide} from 'swiper/react';
import {Mousewheel} from 'swiper/modules';
import {useSwiperHintSlide} from '@hooks/useSwiperHintSlide';
import {useSwiperAutoPlayOnView} from '@hooks/useSwiperAutoPlayOnView';
import {useSwiperWheelReleaseDelay} from '@hooks/useSwiperWheelReleaseDelay';
import styles from './Slider.scss';
import 'swiper/css';
//import 'swiper/css/navigation';

const Slider = forwardRef(({children, overlay = null, ...sliderProps}, ref) => {
	const uniqueId = useId();
	const paginationId = `swiper-pagination-${uniqueId.replace(/[:]/g, '')}`;

	const swiperRef = useRef(null);
	const containerRef = useRef(null);

	const slides = Array.isArray(children) ? children : [children];
	const playInViewPortOnly = sliderProps.playInViewPortOnly ?? false;
	const startHintAnimation = sliderProps.startHintAnimation ?? false;
	const hintAnimationParams = sliderProps.hintAnimationParams ?? {};

	useImperativeHandle(ref, () => ({
		swiper: swiperRef.current?.swiper
	}));

	const sliderSettings = {
		ref: swiperRef,
		modules: sliderProps.modules ?? [Mousewheel],
		spaceBetween: sliderProps.spaceBetween ?? 0,
		slidesPerView: sliderProps.slidesPerView ?? 1,
		centeredSlides: sliderProps.centeredSlides ?? false,
		initialSlide: sliderProps.initialSlide ?? 0,
		watchOverflow: sliderProps.watchOverflow ?? true,
		grid: sliderProps.grid ?? {},
		allowTouchMove: sliderProps.allowTouchMove ?? true,
		freeMode: sliderProps.freeMode ?? false,
		focusableElements: sliderProps.focusableElements ?? 'input, select, option, textarea, video, label',
		containerModifierClass: sliderProps.containerModifierClass ?? '',
		centerInsufficientSlides: sliderProps.centerInsufficientSlides ?? false,
		centeredSlidesBounds: sliderProps.centeredSlidesBounds ?? false,
		mousewheel: sliderProps.mousewheel ?? {
			forceToAxis: true,
			releaseOnEdges: true,
		},
		...(!!sliderProps.showPagination && {
			pagination: {
				clickable: true,
				type: 'bullets',
				el: `#${paginationId}`,
				...sliderProps.pagination,
			},
		}),
		autoplay: sliderProps.autoplay ?? {
			delay: 7000,
			disableOnInteraction: false,
		},
		breakpoints: sliderProps.breakpoints ?? {}
		//////////////////////////////////////////////////////////////////////////
		// EXAMPLE
		// breakpoints: sliderProps.breakpoints ?? {
		// 	0: {
		// 		slidesPerView: 1,
		// 		enabled: true,
		// 		autoplay: {
		// 			delay: 2000,
		// 			disableOnInteraction: true,
		// 		},
		// 	},
		// 	800: {
		// 		slidesPerView: 1,
		// 		enabled: false,
		// 	},
		// },
	}

	const otherSliderSettings = {
		wheelReleaseDelay: sliderProps.wheelReleaseDelay ?? false,
		wheelReleaseDelayOptions: sliderProps.wheelReleaseDelayOptions ?? {
			activationDelay: 150,
			releaseDelay: 350,
			idleTimeout: 400,
			catchMargin: 70,
			threshold: 5
		}
	}

	// Autoplay when navigate to slider
	useSwiperAutoPlayOnView(swiperRef, containerRef, playInViewPortOnly);

	// Hint animation slide when navigate to slider
	useSwiperHintSlide(swiperRef, containerRef, startHintAnimation, hintAnimationParams);

	// Mouse / wheel delay control
	useSwiperWheelReleaseDelay(
	  swiperRef,
	  !!otherSliderSettings.wheelReleaseDelay,
	  otherSliderSettings.wheelReleaseDelayOptions
	);

	return (
	  <div className={styles.sliderContainer} ref={containerRef}>
		  <Swiper {...sliderSettings}>
			  {slides.map((child, i) => (
				<SwiperSlide key={i}>{child}</SwiperSlide>
			  ))}

			  {overlay && (
				<div slot="wrapper-end">
					{overlay}
				</div>
			  )}
		  </Swiper>

		  {!!sliderProps.showPagination && (
			<div id={paginationId} className="custom-pagination swiper-slide--gap-fix" aria-hidden="true"/>
		  )}
	  </div>
	);
});

export default Slider;